import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { usersApi } from '@/api/users'
import type { TeacherContractHistoryEntry, TeacherInfo } from '@/types'
import { ROUTES } from '@/utils/constants'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Breadcrumbs,
  Avatar,
  Alert,
  Divider,
} from '@mui/material'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import Link from '@mui/material/Link'
import { Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { ContractInfo } from '@/components/contracts/ContractInfo'
import { ContractStatusBadge } from '@/components/contracts/ContractStatusBadge'
import { format } from 'date-fns'
import '@/styles/dashboard.css'

function teacherDisplayName(value: TeacherInfo | null | undefined): string {
  if (!value) return '—'
  const full = [value.surname, value.first_name, value.middle_name].filter(Boolean).join(' ')
  return full || value.email || '—'
}

function getInitials(value: TeacherInfo | null) {
  if (!value) return '?'
  const parts = [value.surname, value.first_name].filter(Boolean) as string[]
  if (parts.length) {
    return parts
      .join(' ')
      .split(' ')
      .filter(Boolean)
      .map((p) => p[0]?.toUpperCase())
      .join('')
      .slice(0, 2)
  }
  if (value.email) return value.email[0]?.toUpperCase()
  return '?'
}

export function TeacherViewPage() {
  const { t } = useTranslation()
  const [teacher, setTeacher] = useState<TeacherInfo | null>(null)
  const [history, setHistory] = useState<TeacherContractHistoryEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    usersApi
      .getTeacher()
      .then((data) => {
        if (cancelled) return
        const activeTeacher =
          data.teacher != null
            ? { ...data.teacher, contract: data.contract ?? data.teacher.contract ?? null }
            : null
        const activeContractId = data.contract?.id ?? null
        const pastHistory = data.history.filter((entry) => entry.contract.id !== activeContractId)

        setTeacher(activeTeacher)
        setHistory(pastHistory)

        if (!activeTeacher && pastHistory.length === 0) {
          setError(t('teacherPage.notFound'))
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const e = err as { message?: string }
        setError(e?.message || t('teacherPage.error'))
        setTeacher(null)
        setHistory([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [t])

  const showHistoryOnly = !teacher && history.length > 0

  if (!loading && error && !teacher && history.length === 0) {
    return (
      <DashboardLayout>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Alert severity="info">{error}</Alert>
        </Box>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 }, pb: 0 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            component={RouterLink}
            to={ROUTES.DASHBOARD}
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('teacherPage.title')}</Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ p: { xs: 2, sm: 3 }, pt: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('teacherPage.title')}
          </Typography>
          <PageHelpPopover pageKey="teacher" />
        </Box>

        {showHistoryOnly && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('teacherPage.noActiveTeacher')}
          </Alert>
        )}

        {loading ? (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" height={24} />
            </CardContent>
          </Card>
        ) : (
          <>
            {teacher && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('profile.personalInfo')}
                  </Typography>
                  <Box
                    sx={{
                      mt: 2,
                      display: 'flex',
                      gap: 3,
                      alignItems: 'flex-start',
                      flexDirection: { xs: 'column', sm: 'row' },
                    }}
                  >
                    <Avatar
                      src={teacher.avatar_url ?? undefined}
                      sx={{
                        width: { xs: 96, sm: 120 },
                        height: { xs: 96, sm: 120 },
                        fontSize: { xs: '2rem', sm: '2.5rem' },
                      }}
                    >
                      {getInitials(teacher)}
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('profile.fullName')}
                        </Typography>
                        <Typography variant="body1">{teacherDisplayName(teacher)}</Typography>
                      </Box>
                      {teacher.birthday && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('profile.birthday')}
                          </Typography>
                          <Typography variant="body1">
                            {format(new Date(teacher.birthday), 'dd.MM.yyyy')}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('teacherPage.email')}
                        </Typography>
                        <Typography variant="body1">{teacher.email}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {teacher?.contract && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('contracts.title')}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <ContractInfo contract={teacher.contract} disableGroupLink />
                  </Box>
                </CardContent>
              </Card>
            )}

            {history.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('teacherPage.historyTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('teacherPage.historySubtitle')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {history.map((entry, index) => (
                      <Box key={entry.contract.id}>
                        {index > 0 && <Divider sx={{ mb: 2 }} />}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {teacherDisplayName(entry.teacher)}
                          </Typography>
                          <ContractStatusBadge
                            status={entry.contract.status}
                            label={entry.contract.status_label}
                          />
                        </Box>
                        <ContractInfo contract={entry.contract} disableGroupLink />
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </Box>
    </DashboardLayout>
  )
}
